<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import AssignmentPolicyCard from 'dashboard/components-next/AssignmentPolicy/AssignmentPolicyCard/AssignmentPolicyCard.vue';
import ConfirmDeletePolicyDialog from './components/ConfirmDeletePolicyDialog.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const agentAssignmentsPolicies = useMapGetter(
  'assignmentPolicies/getAssignmentPolicies'
);
const uiFlags = useMapGetter('assignmentPolicies/getUIFlags');
const inboxUiFlags = useMapGetter('assignmentPolicies/getInboxUiFlags');

const confirmDeletePolicyDialogRef = ref(null);
const searchQuery = ref('');

const filteredPolicies = computed(() => {
  if (!searchQuery.value) return agentAssignmentsPolicies.value;
  const query = searchQuery.value.toLowerCase();
  return agentAssignmentsPolicies.value.filter(policy =>
    policy.name?.toLowerCase().includes(query)
  );
});

const goBack = () => {
  router.push({ name: 'assignment_policy_index' });
};

const onClickCreatePolicy = () => {
  router.push({ name: 'agent_assignment_policy_create' });
};

const onClickEditPolicy = id => {
  router.push({ name: 'agent_assignment_policy_edit', params: { id } });
};

const handleFetchInboxes = id => {
  if (inboxUiFlags.value.isFetching) return;
  store.dispatch('assignmentPolicies/getInboxes', id);
};

const handleDelete = id => {
  confirmDeletePolicyDialogRef.value.openDialog(id);
};

const handleDeletePolicy = async policyId => {
  try {
    await store.dispatch('assignmentPolicies/delete', policyId);
    useAlert(
      t(
        'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.SUCCESS_MESSAGE'
      )
    );
    confirmDeletePolicyDialogRef.value.closeDialog();
  } catch (error) {
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.ERROR_MESSAGE')
    );
  }
};

onMounted(() => {
  store.dispatch('assignmentPolicies/get');
});
</script>

<template>
  <div class="policy-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="min-height: 56px"
    >
      <div class="flex items-center gap-3">
        <button
          class="flex items-center gap-1 text-sm transition-colors"
          style="color: #6b7280"
          @click="goBack"
        >
          <span class="i-ph-arrow-left-bold size-4" />
          Back
        </button>
        <span style="color: #d1d5db">|</span>
        <span class="i-ph-shuffle-bold size-5" style="color: #0066ff" />
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{
            $t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')
          }}
        </h1>
        <span
          class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
          style="background: #f9fafb; border: 1px solid #e5e7eb; color: #6b7280"
        >
          {{ agentAssignmentsPolicies.length }} total
        </span>
      </div>
      <button class="ws-btn" @click="onClickCreatePolicy">
        <span class="i-ph-plus-bold size-4" />
        {{
          $t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.CREATE_POLICY'
          )
        }}
      </button>
    </header>

    <!-- Search -->
    <div class="ws-toolbar flex items-center gap-4 px-6 py-3">
      <div class="relative flex-1 max-w-md">
        <span
          class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
          style="color: #9ca3af"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="ws-search reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
          placeholder="Search policies..."
        />
      </div>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <div class="ws-spinner" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredPolicies.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div class="ws-empty-icon">
          <span class="i-ph-shuffle-bold text-amber-400 size-10" />
        </div>
        <h3 class="text-lg font-semibold mb-2" style="color: #111827">
          {{
            $t(
              'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.NO_RECORDS_FOUND'
            )
          }}
        </h3>
        <button class="primary-action-btn mt-4" @click="onClickCreatePolicy">
          <span class="i-ph-plus-bold size-4" />
          Create Policy
        </button>
      </div>

      <!-- Policy Cards -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <AssignmentPolicyCard
          v-for="policy in filteredPolicies"
          :key="policy.id"
          v-bind="policy"
          :is-fetching-inboxes="inboxUiFlags.isFetching"
          @fetch-inboxes="handleFetchInboxes"
          @edit="onClickEditPolicy"
          @delete="handleDelete"
        />
      </div>
    </div>

    <ConfirmDeletePolicyDialog
      ref="confirmDeletePolicyDialogRef"
      @delete="handleDeletePolicy"
    />
  </div>
</template>

<style scoped>
.policy-page,
.dark .policy-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-btn {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 7px 16px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  font-family: 'Excon', system-ui, sans-serif !important;
  background: #111827 !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  transition: all 0.15s ease !important;
  white-space: nowrap !important;
}
.ws-btn:hover {
  background: #1f2937 !important;
}
.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.primary-action-btn:hover {
  background: #1f2937;
}
.ws-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
.ws-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ws-spin 0.9s linear infinite;
}
@keyframes ws-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Assignment policy component-level light theme */
.policy-page {
  --wlab-bg-tertiary: #f9fafb;
  --wlab-bg-hover: #fff7ed;
  --wlab-border-default: #e5e7eb;
  --wlab-text-muted: #9ca3af;
  --wlab-text-secondary: #0066ff;
  --wlab-shadow-card: 0 1px 3px rgba(15, 23, 42, 0.08),
    0 1px 2px rgba(15, 23, 42, 0.05);
  --wlab-shadow-card-hover: 0 14px 24px -16px rgba(15, 23, 42, 0.28);
}

:deep(.wlab-card-modern) {
  background: #ffffff !important;
  outline-color: #e5e7eb !important;
}

:deep(.wlab-card-modern:hover) {
  outline-color: #0066ff !important;
}

:deep(.radio-card) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}

:deep(.radio-card--active) {
  background: #fff7ed !important;
  border-color: #0066ff !important;
}

:deep(.radio-input) {
  accent-color: #0066ff !important;
}

:deep(.outline-n-container) {
  outline-color: #e5e7eb !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2) {
  background: #f9fafb !important;
}

:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
